import type {
  AssetLibraryAssetType,
  AssetLibraryDetail,
  AssetLibraryFolder,
  AssetLibraryListItem,
  AssetLibraryProcessingState,
  AssetLibrarySortDirection,
  AssetLibrarySortField,
} from '@ael/core-api';

import type {
  AssetDetail,
  AssetListItem,
  AssetType,
  Folder,
  ProcessingState,
  SortDirection,
  SortField,
} from '../types';
import { getAssetType, getExtension } from '../types';
import type { AssetScope } from '../domain/client-state/mediaLibraryTypes';
import { ROOT_FOLDER_ID } from '../domain/server-state/folderTree';

/**
 * The wire vocabulary and the screen's vocabulary, and the translation between them.
 *
 * They are deliberately not the same. The server names its enums in PascalCase and dates as Unix
 * seconds, because that is what a C# model serialises to; the UI wants lowercase unions and a
 * `Date`. Neither side should have to bend, so the whole difference is spent here -- one module
 * of pure functions -- rather than leaking into components or into request builders.
 *
 * `type` and `extension` are not on the wire at all: the extension is the only thing that decides
 * either, so both are read off the name here rather than duplicated in the response.
 */

const TO_PROCESSING_STATE: Record<AssetLibraryProcessingState, ProcessingState> = {
  Ready: 'ready',
  Processing: 'processing',
  Failed: 'failed',
};

const TO_SERVER_PROCESSING_STATE: Record<ProcessingState, AssetLibraryProcessingState> = {
  ready: 'Ready',
  processing: 'Processing',
  failed: 'Failed',
};

const TO_SERVER_ASSET_TYPE: Record<AssetType, AssetLibraryAssetType> = {
  image: 'Image',
  audio: 'Audio',
  video: 'Video',
  document: 'Document',
  subtitle: 'Subtitle',
  web: 'Web',
};

const TO_SERVER_SORT_FIELD: Record<SortField, AssetLibrarySortField> = {
  uploadDate: 'UploadedAt',
  name: 'Name',
  size: 'Size',
};

const TO_SERVER_SORT_DIRECTION: Record<SortDirection, AssetLibrarySortDirection> = {
  asc: 'Asc',
  desc: 'Desc',
};

/** Everything `queryAssets` takes -- the company is already bound into the client. */
export type AssetQueryParams = {
  skip: number;
  limit: number;
  sortField: AssetLibrarySortField;
  sortDirection: AssetLibrarySortDirection;
  directoryId: string | null;
  search: string | null;
  types: AssetLibraryAssetType[] | null;
  states: AssetLibraryProcessingState[] | null;
};

/**
 * Paging is not exposed yet, so every query asks for the whole match set. `limit: 0` is the
 * server's own "no page size", not an empty page -- see `AssetQueryModel`.
 */
const NO_PAGING = { skip: 0, limit: 0 } as const;

export function toAssetQueryParams({ folderId, query }: AssetScope): AssetQueryParams {
  return {
    ...NO_PAGING,
    sortField: TO_SERVER_SORT_FIELD[query.sort.field],
    sortDirection: TO_SERVER_SORT_DIRECTION[query.sort.direction],
    // A search spans the whole library -- finding a file must not depend on already knowing which
    // folder it is in -- and the root is expressed as "no directory", which is what the server
    // scopes to when it gets none.
    directoryId: query.search !== '' || folderId === ROOT_FOLDER_ID ? null : folderId,
    search: query.search === '' ? null : query.search,
    types: query.types.length === 0 ? null : query.types.map((type) => TO_SERVER_ASSET_TYPE[type]),
    states: query.states.length === 0 ? null : query.states.map((state) => TO_SERVER_PROCESSING_STATE[state]),
  };
}

/**
 * What a listing row and a detail have in common.
 *
 * Split out because `folderId` is not among it: only the listing states which folder an asset is
 * in, since that is what the query's scoping walk works out on the way to deciding what is in
 * view. A detail is fetched by id and has no such walk behind it.
 */
function toAssetBase(item: Omit<AssetLibraryListItem, 'directoryId'>): Omit<AssetListItem, 'folderId'> {
  return {
    id: item.id,
    name: item.name,
    // An asset whose extension the client does not know still has to be drawn, so it falls back
    // to the neutral type rather than being dropped from the listing.
    type: getAssetType(item.name) ?? 'document',
    extension: getExtension(item.name),
    size: item.size,
    uploadDate: new Date(item.uploadedAt * 1_000),
    state: TO_PROCESSING_STATE[item.state],
    thumbnailUrl: item.thumbnailUrl ?? undefined,
  };
}

export function toAssetListItem(item: AssetLibraryListItem): AssetListItem {
  return { ...toAssetBase(item), folderId: item.directoryId };
}

export function toAssetDetail(detail: AssetLibraryDetail): AssetDetail {
  return {
    ...toAssetBase(detail),
    mediaUrl: detail.mediaUrl,
    imageWidth: detail.dimensions?.width,
    imageHeight: detail.dimensions?.height,
    // Omitted rather than set to null, so the domain object stays clean under
    // `exactOptionalPropertyTypes` and `attribution` being present means there is one.
    ...(detail.attribution ? { attribution: detail.attribution } : {}),
  };
}

/**
 * The root is not in the server's list -- the UI draws it itself -- so a folder sitting
 * directly in it arrives with no parent, which `buildFolderTree` attaches to the root node.
 */
export function toFolder(folder: AssetLibraryFolder): Folder {
  return { id: folder.id, name: folder.name, parentId: folder.parentId };
}
